import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import {useFluxStore} from 'client/framework';
import React from 'react';

import {Banner} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {useShallow} from 'zustand/react/shallow';

import {
  pendingTeacherGradingText as translatedPendingText,
  studentText as translatedStudentText,
  useQuestionTranslationStore
} from 'client/components/QuestionTypes/shared/TranslationSection';

import type {FrqGradingStatus} from './frqGradingStatus';
import useFrqPendingTeacherGrading from './hooks/useFrqPendingTeacherGrading';

const studentText = 'Your response has been submitted and will be graded outside of Albert.';
const teacherText = 'Free response questions are automatically graded as correct.';
const gradingInProgressText = 'Grading in progress';
const pendingText =
  'Your response was submitted and is being graded by your teacher. Check back later to see your grade.';

interface Props {
  /** The question this submission answers, used to find the student's own guess. */
  questionId?: string;
  /**
   * The viewed guess's manual-grading state, where a teacher-gated read supplies one. Absent in
   * independent practice and on a student's own view, which have no grading to report.
   */
  gradingStatus?: FrqGradingStatus;
  /** Set where the response belongs to someone else — a teacher reading a student's submission. */
  isViewingStudentWork?: boolean;
}

/** The banner shape shared by every state where a grade is still on its way. */
function AwaitingGradeBanner({
  children,
  'data-testid': dataTestId
}: {
  children: React.ReactNode;
  'data-testid': string;
}) {
  return (
    <div className='mb-x4'>
      <Banner data-testid={dataTestId} type='info'>
        <Banner.Icon
          className='pr-x0! mr-x2 self-center'
          icon='arrows-rotate'
          iconStyle='regular'
        />
        {children}
      </Banner>
    </div>
  );
}

/**
 * Where a submitted free response stands for whoever is reading it: awaiting a teacher's grade for
 * the student who wrote it, awaiting one for the teacher who owes it, or graded outside Albert.
 *
 * A graded guess never reaches here — `SubmittedFrqStatus` renders the correctness banner for that
 * instead, so this covers only the states where no grade exists yet.
 */
export default function CompletenessBanner({
  questionId,
  gradingStatus,
  isViewingStudentWork
}: Props) {
  const isStudent = useFluxStore(sessionStore, (s) => s.isStudent());
  const isPendingTeacherGrading = useFrqPendingTeacherGrading(questionId);
  const {isFeatureEnabled: isTeacherGradingEnabled, isLoading: isFlagLoading} =
    useIsFeatureFlagEnabled('frq_teacher_grading');
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  if (isPendingTeacherGrading) {
    return (
      <AwaitingGradeBanner data-testid='pending-teacher-grading-banner'>
        {showTranslations ? translatedPendingText : pendingText}
      </AwaitingGradeBanner>
    );
  }

  // Reading a student's submission, the guess's status picks the banner, so hold the render until
  // the flag and that status resolve. Falling through meanwhile would flash the auto-graded copy
  // over a response nobody has graded.
  if (
    !isStudent &&
    isViewingStudentWork &&
    (isFlagLoading || (isTeacherGradingEnabled && !gradingStatus))
  ) {
    return null;
  }

  const isAwaitingTeacherGrade =
    gradingStatus === 'ungraded' || gradingStatus === 'grading_in_progress';

  if (!isStudent && isTeacherGradingEnabled && isAwaitingTeacherGrade) {
    return (
      <AwaitingGradeBanner data-testid='teacher-grading-in-progress-banner'>
        {gradingInProgressText}
      </AwaitingGradeBanner>
    );
  }

  const bannerText =
    showTranslations && isStudent ? translatedStudentText : isStudent ? studentText : teacherText;

  return (
    <div className='mb-x4'>
      <Banner type='positive'>
        <Banner.Icon icon='check' />
        {bannerText}
      </Banner>
    </div>
  );
}
